// @ts-nocheck
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { productLineProps } from "@store/Themes/common/props/productLine";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
/* Store Custom Code - End */

// Store Custom Code - extends CartLineQuantityPresentationProps for extendedStyles/overridedStyles
interface OwnProps extends CartLineQuantityPresentationProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    // Store Removed Code - extendedStyles (now provided via CartLineQuantityPresentationProps)
    label?: string;
    // Store Custom Code
    noLabel?: boolean;
    overridedQty?: number;
    error?: React.ReactNode;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

/* Store Custom Code - Start */
export interface CartLineQuantityPresentationProps extends ExpandStyles<CartLineQuantityStyles> {}
/* Store Custom Code - End */

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    ...productLineProps.quantity,
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // Store Custom Code
    overridedStyles,
    noLabel,
    overridedQty,
    error,
}) => {
    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseInt(value, 10); // Store Custom Code
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        (overridedQty ?? cartLine.qtyOrdered!).toString(), // Store Custom Code
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    // Store Custom Code - ExpandStyles merge so extendedStyles/overridedStyles are applied
    const styles = useExpandProps(cartLineQuantityStyles, extendedStyles, overridedStyles);

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <TextField
                {...styles.editableQuantityTextField}
                type="number"
                min={0}
                max={999999}
                label={noLabel ? undefined : label || translate("QTY")} // Store Custom Code
                value={value}
                disabled={!cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]}
                onChange={changeHandler}
                onKeyDown={keyDownHandler}
                onBlur={blurHandler}
                onClick={event => event.currentTarget.focus()}
                data-test-selector="cartline_qty"
                // Store Custom Code
                error={error}
            />
        );
    }

    return (
        <SmallHeadingAndText
            {...styles.readOnlyQuantityText}
            heading={noLabel ? "" : label || translate("QTY")} // Store Custom Code
            text={value} // Store Custom Code
            data-test-selector="quoteDetail_cardLine_qty"
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
